// Renders a Parentum user-data export JSON blob into a fully branded PDF
// (matching headers/footers/logos/disclaimer used by every other PDF in the
// app). Used by both the user-facing download in Privacy Center AND the
// admin-facing download in the Admin Panel.

import jsPDF from 'jspdf';
import { addPdfHeader, addPdfFooterToAllPages, ensureImagesLoaded } from './pdfBranding';

// Friendly labels + ordering for every section we know about. Anything not
// listed gets auto-rendered at the end with its raw collection name.
var SECTION_LABELS = {
  account: 'Account Information',
  user_preferences: 'Preferences',
  user_safety_settings: 'Safety Settings',
  user_compliance: 'Compliance',
  tos_acceptances: 'Terms of Service Acceptance History',
  marketing_consent: 'Marketing Consent',
  party_info: 'Party Information',
  school_times: 'School / Care Times',
  children: 'Children Listed on Account',

  journal_entries: 'Journal Entries',
  calendar_events: 'Calendar Events',
  calendar_assignments: 'Calendar Custody Assignments',
  calendar_event_shares: 'Shared Calendar Invites',
  change_requests: 'Calendar Change Requests',
  google_calendar_connections: 'Google Calendar Connections',
  google_calendar_events: 'Google Calendar Synced Events',
  google_oauth_states: 'Google OAuth Sessions',

  coparent_links: 'Co-Parent Links',
  coparent_messages: 'Co-Parent Messages',
  chat_messages: 'Chat Messages',
  messages: 'Messages',
  messaging_preferences: 'Messaging Preferences',
  messaging_suspensions: 'Messaging Suspensions',
  blocked_message_incidents: 'Blocked Message Incidents',

  companion_profiles: 'AI Companion Profiles',
  companion_messages: 'AI Companion Conversations',
  companion_analyses: 'AI Companion Analyses',

  case_files: 'Case Files',
  plan_drafts: 'Parenting Plan Drafts',
  plan_analyses: 'Plan Analyses',
  plan_scans: 'Plan Scans',

  course_enrollments: 'Course Enrollments',
  course_purchases: 'Course Purchases',
  bundle_purchases: 'Bundle Purchases',
  lesson_purchases: 'Lesson Purchases',
  lesson_completions: 'Lesson Completions',
  course_unlocks: 'Course Unlocks',
  course_disclaimers: 'Course Disclaimer Acknowledgements',
  quiz_attempts: 'Quiz Attempts',
  quiz_results: 'Quiz Results',
  certificates: 'Certificates Earned',

  reward_ledger: 'Rewards / Points Ledger',

  legal_requests: 'Legal Requests',
  bra_checkouts: 'Business Record Affidavit Checkouts',

  payment_transactions: 'Payment Transactions',
  upgrade_history: 'Subscription Upgrade History',
  promo_redemptions: 'Promo Code Redemptions',

  notifications: 'Notifications',

  video_call_rooms: 'Video Call Records',
  video_call_usage: 'Video Call Usage History',
  video_backgrounds: 'Saved Virtual Backgrounds',

  user_sessions: 'Active Sessions (last 200)',
  audit_logs: 'Audit Log Entries',
};

var SECTION_ORDER = [
  'account',
  'children',
  'user_preferences',
  'user_safety_settings',
  'user_compliance',
  'tos_acceptances',
  'party_info',
  'school_times',
  'coparent_links',
  'coparent_messages',
  'chat_messages',
  'messages',
  'messaging_preferences',
  'messaging_suspensions',
  'blocked_message_incidents',
  'journal_entries',
  'calendar_events',
  'calendar_assignments',
  'calendar_event_shares',
  'change_requests',
  'google_calendar_connections',
  'google_calendar_events',
  'companion_profiles',
  'companion_messages',
  'companion_analyses',
  'case_files',
  'plan_drafts',
  'plan_analyses',
  'plan_scans',
  'course_enrollments',
  'course_purchases',
  'bundle_purchases',
  'lesson_purchases',
  'lesson_completions',
  'course_unlocks',
  'course_disclaimers',
  'quiz_attempts',
  'quiz_results',
  'certificates',
  'reward_ledger',
  'legal_requests',
  'bra_checkouts',
  'payment_transactions',
  'upgrade_history',
  'promo_redemptions',
  'notifications',
  'video_call_rooms',
  'video_call_usage',
  'video_backgrounds',
  'user_sessions',
  'audit_logs',
];

function fmtVal(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') {
    // ISO date → readable
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      try {
        var d = new Date(v);
        if (!isNaN(d.getTime())) return d.toUTCString();
      } catch (e) { /* fallthrough */ }
    }
    return v;
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '(empty)';
    try { return JSON.stringify(v); } catch (e) { return '[array]'; }
  }
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch (e) { return '[object]'; }
  }
  return String(v);
}

function flattenObj(obj, prefix) {
  // Returns [[label, value], ...] from a (possibly nested) object.
  var out = [];
  if (!obj || typeof obj !== 'object') return out;
  Object.keys(obj).forEach(function (k) {
    var v = obj[k];
    var label = prefix ? prefix + '.' + k : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out = out.concat(flattenObj(v, label));
    } else {
      out.push([label, fmtVal(v)]);
    }
  });
  return out;
}

export async function buildUserDataPdf(data) {
  await ensureImagesLoaded();

  var pdf = new jsPDF({ unit: 'mm', format: 'letter' });
  var pw = pdf.internal.pageSize.getWidth();
  var ph = pdf.internal.pageSize.getHeight();
  var bottomLimit = ph - 22; // leave room for footer
  var leftMargin = 18;
  var rightMargin = 18;
  var contentWidth = pw - leftMargin - rightMargin;

  function ensureSpace(y, needed) {
    if (y + needed > bottomLimit) {
      pdf.addPage();
      return addPdfHeader(pdf);
    }
    return y;
  }

  // ---------- Page 1 cover ----------
  var y = addPdfHeader(pdf);
  y += 4;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(30, 26, 62);
  pdf.text('Parentum — Personal Data Export', leftMargin, y);
  y += 8;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(60, 60, 80);
  var account = (data && data.account) || {};
  pdf.text('Account holder: ' + (account.name || account.email || '(unknown)'), leftMargin, y);
  y += 5;
  pdf.text('Email: ' + (account.email || '—'), leftMargin, y);
  y += 5;
  pdf.text('Subscription: ' + (account.subscription_plan || 'free') + (account.billing_interval ? ' (' + account.billing_interval + ')' : ''), leftMargin, y);
  y += 5;
  pdf.text('Generated: ' + (data && data.exported_at ? new Date(data.exported_at).toUTCString() : new Date().toUTCString()), leftMargin, y);
  y += 5;
  if (account.is_deleted) {
    pdf.setTextColor(180, 30, 30);
    pdf.text('ACCOUNT STATUS: DELETED on ' + (account.deleted_at || '—'), leftMargin, y);
    pdf.setTextColor(60, 60, 80);
    y += 5;
  }
  y += 4;

  pdf.setFontSize(9);
  pdf.setTextColor(80, 80, 100);
  var introText =
    'This document is a comprehensive export of every record Parentum holds for the account ' +
    'identified above. Sections are organised by data category. Empty sections indicate no records ' +
    'exist for that category.';
  var introLines = pdf.splitTextToSize(introText, contentWidth);
  pdf.text(introLines, leftMargin, y);
  y += introLines.length * 4 + 6;

  // ---------- Sections ----------
  // Build the actual ordering: known order first, then any unknown collections.
  var keys = Object.keys(data || {}).filter(function (k) {
    return k !== 'exported_at' && Object.prototype.hasOwnProperty.call(data, k);
  });
  var ordered = SECTION_ORDER.filter(function (k) { return keys.indexOf(k) !== -1; });
  keys.forEach(function (k) { if (ordered.indexOf(k) === -1) ordered.push(k); });

  ordered.forEach(function (key) {
    var value = data[key];
    var label = SECTION_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });

    // Section header.
    y = ensureSpace(y, 14);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.setTextColor(45, 37, 96);
    pdf.text(label, leftMargin, y);
    y += 2;
    pdf.setDrawColor(168, 170, 212);
    pdf.setLineWidth(0.4);
    pdf.line(leftMargin, y, pw - rightMargin, y);
    y += 6;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(40, 40, 60);

    // Render based on shape: object, array of objects, or scalar.
    if (value === null || value === undefined) {
      pdf.setTextColor(140, 140, 160);
      pdf.text('No records.', leftMargin, y);
      y += 6;
    } else if (Array.isArray(value)) {
      if (value.length === 0) {
        pdf.setTextColor(140, 140, 160);
        pdf.text('No records.', leftMargin, y);
        y += 6;
      } else {
        value.forEach(function (item, idx) {
          y = ensureSpace(y, 12);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(9.5);
          pdf.setTextColor(60, 53, 102);
          pdf.text('Record #' + (idx + 1), leftMargin, y);
          y += 4.5;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.5);
          pdf.setTextColor(40, 40, 60);

          var rows = (item && typeof item === 'object' && !Array.isArray(item))
            ? flattenObj(item, '')
            : [['value', fmtVal(item)]];
          rows.forEach(function (kv) {
            var line = '  ' + kv[0] + ': ' + kv[1];
            var wrapped = pdf.splitTextToSize(line, contentWidth);
            wrapped.forEach(function (l) {
              y = ensureSpace(y, 4);
              pdf.text(l, leftMargin, y);
              y += 3.6;
            });
          });
          y += 2;
        });
      }
    } else if (typeof value === 'object') {
      var rows = flattenObj(value, '');
      if (rows.length === 0) {
        pdf.setTextColor(140, 140, 160);
        pdf.text('No records.', leftMargin, y);
        y += 6;
      } else {
        rows.forEach(function (kv) {
          var line = kv[0] + ': ' + kv[1];
          var wrapped = pdf.splitTextToSize(line, contentWidth);
          wrapped.forEach(function (l) {
            y = ensureSpace(y, 4);
            pdf.text(l, leftMargin, y);
            y += 3.8;
          });
        });
      }
    } else {
      pdf.text(fmtVal(value), leftMargin, y);
      y += 5;
    }
    y += 4;
  });

  addPdfFooterToAllPages(pdf);
  return pdf;
}

export async function downloadUserDataPdf(data, filenameHint) {
  var pdf = await buildUserDataPdf(data);
  var safeName = ((filenameHint || data?.account?.email || data?.account?.name || 'parentum-user') + '')
    .replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 60);
  var stamp = new Date().toISOString().slice(0, 10);
  pdf.save('parentum-data-' + safeName + '-' + stamp + '.pdf');
}
